<template>
  <div class="w-full p-4 my-6">
    <smm-sub-nav />

    <div class="flex flex-col lg:flex-row gap-4 min-h-[70vh]">
      <aside class="w-full lg:w-[20%] shrink-0">
        <nav class="bg-white rounded-lg border border-gray-100 shadow-sm p-2 space-y-1">
          <router-link
            v-for="item in menuItems"
            :key="item.id"
            :to="{ name: item.route }"
            class="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition"
            :class="section === item.id
              ? 'bg-gray-900 text-white hover:bg-gray-900 hover:text-white'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-700'"
          >
            {{ item.label }}
          </router-link>
        </nav>
      </aside>

      <section class="flex-1 min-w-0 bg-white rounded-lg border border-gray-100 shadow-sm p-4 sm:p-6">
        <template v-if="section === 'channels'">
          <div class="mb-4">
            <h2 class="text-lg font-semibold text-gray-900">{{ $t('smm.settings.channels_title') }}</h2>
            <p class="text-sm text-gray-500 mt-0.5">{{ $t('smm.settings.channels_subtitle') }}</p>
          </div>

          <div v-if="loading" class="text-sm text-gray-500 py-8 text-center">{{ $t('common.loading') }}</div>
          <div v-else-if="loadError" class="text-sm text-red-600 py-4">{{ loadError }}</div>
          <div v-else class="grid grid-cols-1 xl:grid-cols-[240px_minmax(0,1fr)] gap-4">
            <div class="space-y-1">
              <button
                v-for="shop in shops"
                :key="shop.id"
                type="button"
                class="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left border transition"
                :class="selectedId === shop.id
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-200 hover:border-gray-400 bg-white text-gray-800'"
                @click="selectChannel(shop.id)"
              >
                <span
                  class="w-3 h-3 rounded-full shrink-0 border border-black/10"
                  :style="{ backgroundColor: shop.color || '#111827' }"
                />
                <span class="truncate">{{ shop.name }}</span>
                <span
                  v-if="shop.is_active === false"
                  class="ml-auto text-[10px] uppercase opacity-70"
                >{{ $t('smm.settings.inactive') }}</span>
              </button>
              <p v-if="!shops.length" class="text-sm text-gray-500 px-1 py-4">
                {{ $t('smm.settings.channels_empty') }}
              </p>
              <button
                type="button"
                class="w-full mt-4 px-3 py-2 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 hover:border-gray-400"
                @click="createChannel"
              >
                {{ $t('smm.settings.channel_add') }}
              </button>
            </div>

            <div v-if="form" class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="text-sm text-gray-600">{{ $t('common.name') }}</label>
                  <input v-model="form.name" class="w-full border rounded-lg p-2.5 mt-1 text-sm" />
                </div>
                <div>
                  <label class="text-sm text-gray-600">{{ $t('smm.settings.slug') }}</label>
                  <input v-model="form.slug" class="w-full border rounded-lg p-2.5 mt-1 text-sm font-mono" />
                </div>
                <div>
                  <label class="text-sm text-gray-600">{{ $t('smm.settings.color') }}</label>
                  <div class="flex items-center gap-2 mt-1">
                    <input v-model="form.color" type="color" class="h-10 w-12 border rounded cursor-pointer bg-white" />
                    <input v-model="form.color" type="text" class="flex-1 border rounded-lg p-2.5 text-sm font-mono" placeholder="#111827" />
                  </div>
                </div>
                <div>
                  <label class="text-sm text-gray-600">{{ $t('smm.settings.homepage_url') }}</label>
                  <input
                    v-model="form.homepage_url"
                    type="url"
                    :placeholder="$t('common.url_placeholder')"
                    class="w-full border rounded-lg p-2.5 mt-1 text-sm"
                  />
                </div>
                <div class="sm:col-span-2">
                  <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                    <input v-model="form.is_active" type="checkbox" class="h-4 w-4 rounded border-gray-300" />
                    {{ $t('smm.settings.is_active') }}
                  </label>
                </div>
              </div>

              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.settings.base_prompt') }}</label>
                <textarea
                  v-model="form.base_prompt"
                  rows="6"
                  class="w-full border rounded-lg p-2.5 mt-1 text-sm resize-y min-h-[8rem]"
                  :placeholder="$t('smm.settings.base_prompt_placeholder')"
                />
              </div>

              <div class="space-y-2">
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <label class="text-sm text-gray-600">{{ $t('smm.settings.media') }}</label>
                  <p class="text-xs text-gray-500">{{ $t('smm.settings.media_hint') }}</p>
                </div>
                <div
                  class="grid gap-3"
                  style="grid-template-columns: repeat(auto-fill, minmax(160px, 160px));"
                >
                  <media-upload-panel
                    ref="uploadPanel"
                    v-model="pendingFiles"
                    compact
                    @play-video="openVideoPreview"
                  />
                  <div
                    v-for="(file, index) in form.files"
                    :key="'shop-file-' + file.id"
                    class="relative rounded-lg overflow-hidden border aspect-square group bg-gray-100 cursor-grab active:cursor-grabbing w-[160px] h-[160px]"
                    draggable="true"
                    @dragstart="onFileDragStart(index, $event)"
                    @dragover.prevent
                    @drop.prevent="onFileDrop(index)"
                  >
                    <a
                      v-if="file.type === 'image'"
                      :href="file.url"
                      target="_blank"
                      rel="noopener"
                      class="block w-full h-full"
                      draggable="false"
                      @click.prevent
                      @dragstart.stop.prevent
                    >
                      <img
                        :src="file.url"
                        :alt="file.original_name || form.name"
                        class="w-full h-full object-cover"
                        draggable="false"
                      />
                    </a>
                    <template v-else>
                      <video
                        :src="file.url"
                        class="w-full h-full object-cover pointer-events-none"
                        muted
                        playsinline
                        preload="metadata"
                      />
                      <button
                        type="button"
                        class="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/40"
                        @click="openVideoPreview({ src: file.url, title: file.original_name || $t('smm.media.video') })"
                      >
                        <span class="w-8 h-8 rounded-full bg-white/95 flex items-center justify-center shadow pl-px">▶</span>
                      </button>
                    </template>
                    <span class="absolute top-1.5 left-1.5 z-10 text-[10px] px-1.5 py-0.5 rounded bg-black/55 text-white pointer-events-none">
                      {{ index + 1 }}
                    </span>
                    <button
                      type="button"
                      class="absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-full bg-red-600 text-white text-sm leading-none opacity-0 group-hover:opacity-100"
                      @click="deleteFile(file)"
                      @mousedown.stop
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>

              <p v-if="formError" class="text-sm text-red-600 whitespace-pre-line">{{ formError }}</p>

              <div class="flex flex-wrap justify-between gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  class="px-3 py-2 text-sm rounded-lg text-red-600 hover:bg-red-50"
                  :disabled="saving"
                  @click="removeChannel"
                >
                  {{ $t('common.delete') }}
                </button>
                <button
                  type="button"
                  class="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  :disabled="saving || !form.name.trim()"
                  @click="saveChannel"
                >
                  {{ saving ? $t('common.saving') : $t('common.save') }}
                </button>
              </div>
            </div>
            <div v-else class="text-sm text-gray-500 py-10 text-center">
              {{ $t('smm.settings.channel_select') }}
            </div>
          </div>
        </template>

        <template v-else-if="section === 'prompts'">
          <div class="mb-4">
            <h2 class="text-lg font-semibold text-gray-900">{{ $t('smm.settings.prompts_title') }}</h2>
            <p class="text-sm text-gray-500 mt-0.5">{{ $t('smm.settings.prompts_subtitle') }}</p>
          </div>

          <div v-if="promptsLoading" class="text-sm text-gray-500 py-8 text-center">{{ $t('common.loading') }}</div>
          <div v-else-if="promptsLoadError" class="text-sm text-red-600 py-4">{{ promptsLoadError }}</div>
          <div v-else class="grid grid-cols-1 xl:grid-cols-[240px_minmax(0,1fr)] gap-4">
            <div class="space-y-1">
              <button
                v-for="prompt in prompts"
                :key="prompt.id"
                type="button"
                class="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left border transition"
                :class="selectedPromptId === prompt.id
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-200 hover:border-gray-400 bg-white text-gray-800'"
                @click="selectPrompt(prompt.id)"
              >
                <span class="truncate">{{ prompt.name }}</span>
                <span
                  class="ml-auto shrink-0 text-[10px] font-mono uppercase opacity-70"
                  :class="selectedPromptId === prompt.id ? 'text-white/80' : 'text-gray-500'"
                >{{ prompt.slug }}</span>
              </button>
              <p v-if="!prompts.length" class="text-sm text-gray-500 px-1 py-4">
                {{ $t('smm.settings.prompts_empty') }}
              </p>
            </div>

            <div v-if="promptForm" class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="text-sm text-gray-600">{{ $t('common.name') }}</label>
                  <input v-model="promptForm.name" class="w-full border rounded-lg p-2.5 mt-1 text-sm" />
                </div>
                <div>
                  <label class="text-sm text-gray-600">{{ $t('smm.settings.prompt_slug') }}</label>
                  <input
                    :value="promptForm.slug"
                    type="text"
                    readonly
                    class="w-full border rounded-lg p-2.5 mt-1 text-sm font-mono bg-gray-50 text-gray-600"
                  />
                  <p class="text-xs text-gray-500 mt-1">{{ $t('smm.settings.prompt_slug_locked') }}</p>
                </div>
                <div class="sm:col-span-2">
                  <label class="text-sm text-gray-600">{{ $t('smm.settings.prompt_description') }}</label>
                  <input
                    v-model="promptForm.description"
                    type="text"
                    class="w-full border rounded-lg p-2.5 mt-1 text-sm"
                  />
                </div>
              </div>

              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.settings.prompt_body') }}</label>
                <textarea
                  v-model="promptForm.body"
                  rows="14"
                  class="w-full border rounded-lg p-2.5 mt-1 text-sm resize-y min-h-[16rem] font-mono"
                  :placeholder="$t('smm.settings.prompt_body_placeholder')"
                />
              </div>

              <p v-if="promptFormError" class="text-sm text-red-600 whitespace-pre-line">{{ promptFormError }}</p>

              <div class="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  class="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  :disabled="promptSaving || !promptForm.name.trim() || !promptForm.body.trim()"
                  @click="savePrompt"
                >
                  {{ promptSaving ? $t('common.saving') : $t('common.save') }}
                </button>
              </div>
            </div>
            <div v-else class="text-sm text-gray-500 py-10 text-center">
              {{ $t('smm.settings.prompt_select') }}
            </div>
          </div>
        </template>

        <template v-else-if="section === 'campaigns'">
          <div class="mb-4">
            <h2 class="text-lg font-semibold text-gray-900">{{ $t('smm.settings.campaigns_title') }}</h2>
            <p class="text-sm text-gray-500 mt-0.5">{{ $t('smm.settings.campaigns_subtitle') }}</p>
          </div>

          <div v-if="campaignsLoading" class="text-sm text-gray-500 py-8 text-center">{{ $t('common.loading') }}</div>
          <div v-else-if="campaignsLoadError" class="text-sm text-red-600 py-4">{{ campaignsLoadError }}</div>
          <div v-else class="grid grid-cols-1 xl:grid-cols-[240px_minmax(0,1fr)] gap-4">
            <div class="space-y-1">
              <button
                v-for="campaign in campaigns"
                :key="campaign.id"
                type="button"
                class="w-full flex flex-col gap-0.5 px-3 py-2 rounded-lg text-sm text-left border transition"
                :class="selectedCampaignId === campaign.id
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-200 hover:border-gray-400 bg-white text-gray-800'"
                @click="selectCampaign(campaign.id)"
              >
                <span class="truncate font-medium">{{ campaign.name }}</span>
                <span
                  class="text-[11px] opacity-70"
                  :class="selectedCampaignId === campaign.id ? 'text-white/80' : 'text-gray-500'"
                >{{ formatCampaignDates(campaign) }}</span>
              </button>
              <p v-if="!campaigns.length" class="text-sm text-gray-500 px-1 py-4">
                {{ $t('smm.settings.campaigns_empty') }}
              </p>
              <button
                type="button"
                class="w-full mt-4 px-3 py-2 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 hover:border-gray-400"
                @click="createCampaign"
              >
                {{ $t('smm.settings.campaign_add') }}
              </button>
            </div>

            <div v-if="campaignForm" class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="sm:col-span-2">
                  <label class="text-sm text-gray-600">{{ $t('common.name') }}</label>
                  <input
                    v-model="campaignForm.name"
                    class="w-full border rounded-lg p-2.5 mt-1 text-sm"
                    placeholder="Back to school"
                  />
                </div>
                <div>
                  <label class="text-sm text-gray-600">{{ $t('smm.settings.campaign_starts_on') }}</label>
                  <input
                    v-model="campaignForm.starts_on"
                    type="date"
                    class="w-full border rounded-lg p-2.5 mt-1 text-sm"
                  />
                </div>
                <div>
                  <label class="text-sm text-gray-600">
                    {{ $t('smm.settings.campaign_ends_on') }}
                    <span class="text-gray-400 font-normal">({{ $t('smm.settings.campaign_ends_on_optional') }})</span>
                  </label>
                  <input
                    v-model="campaignForm.ends_on"
                    type="date"
                    class="w-full border rounded-lg p-2.5 mt-1 text-sm"
                  />
                </div>
                <div>
                  <label class="text-sm text-gray-600">{{ $t('smm.settings.campaign_priority') }}</label>
                  <input
                    v-model.number="campaignForm.priority"
                    type="number"
                    min="1"
                    max="10"
                    class="w-full border rounded-lg p-2.5 mt-1 text-sm"
                  />
                </div>
                <div class="flex items-end">
                  <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none pb-2.5">
                    <input v-model="campaignForm.is_active" type="checkbox" class="h-4 w-4 rounded border-gray-300" />
                    {{ $t('smm.settings.is_active') }}
                  </label>
                </div>
              </div>

              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.settings.campaign_description') }}</label>
                <textarea
                  v-model="campaignForm.description"
                  rows="10"
                  class="w-full border rounded-lg p-2.5 mt-1 text-sm resize-y min-h-[12rem]"
                  :placeholder="$t('smm.settings.campaign_description_placeholder')"
                />
              </div>

              <p v-if="campaignFormError" class="text-sm text-red-600 whitespace-pre-line">{{ campaignFormError }}</p>

              <div class="flex flex-wrap justify-between gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  class="px-3 py-2 text-sm rounded-lg text-red-600 hover:bg-red-50"
                  :disabled="campaignSaving"
                  @click="removeCampaign"
                >
                  {{ $t('common.delete') }}
                </button>
                <button
                  type="button"
                  class="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  :disabled="campaignSaving || !campaignForm.name.trim() || !campaignForm.starts_on"
                  @click="saveCampaign"
                >
                  {{ campaignSaving ? $t('common.saving') : $t('common.save') }}
                </button>
              </div>
            </div>
            <div v-else class="text-sm text-gray-500 py-10 text-center">
              {{ $t('smm.settings.campaign_select') }}
            </div>
          </div>
        </template>

        <template v-else-if="section === 'platforms'">
          <div class="mb-4">
            <h2 class="text-lg font-semibold text-gray-900">{{ $t('smm.settings.platforms_title') }}</h2>
            <p class="text-sm text-gray-500 mt-0.5">{{ $t('smm.settings.platforms_subtitle') }}</p>
          </div>

          <div v-if="platformsLoading" class="text-sm text-gray-500 py-8 text-center">{{ $t('common.loading') }}</div>
          <div v-else-if="platformsLoadError" class="text-sm text-red-600 py-4">{{ platformsLoadError }}</div>
          <div v-else class="grid grid-cols-1 xl:grid-cols-[240px_minmax(0,1fr)] gap-4">
            <div class="space-y-1">
              <button
                v-for="platform in platforms"
                :key="platform.id"
                type="button"
                class="w-full flex flex-col gap-0.5 px-3 py-2 rounded-lg text-sm text-left border transition"
                :class="selectedPlatformId === platform.id
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-200 hover:border-gray-400 bg-white text-gray-800'"
                @click="selectPlatform(platform.id)"
              >
                <span class="truncate font-medium">{{ platform.name }}</span>
                <span
                  class="text-[11px] opacity-70"
                  :class="selectedPlatformId === platform.id ? 'text-white/80' : 'text-gray-500'"
                >{{ platform.slug }}</span>
              </button>
              <p v-if="!platforms.length" class="text-sm text-gray-500 px-1 py-4">
                {{ $t('smm.settings.platforms_empty') }}
              </p>
              <button
                type="button"
                class="w-full mt-4 px-3 py-2 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 hover:border-gray-400"
                @click="createPlatform"
              >
                {{ $t('smm.settings.platform_add') }}
              </button>
            </div>

            <div v-if="platformForm" class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="text-sm text-gray-600">{{ $t('common.name') }}</label>
                  <input v-model="platformForm.name" class="w-full border rounded-lg p-2.5 mt-1 text-sm" />
                </div>
                <div>
                  <label class="text-sm text-gray-600">{{ $t('smm.settings.platform_slug') }}</label>
                  <input v-model="platformForm.slug" class="w-full border rounded-lg p-2.5 mt-1 text-sm" />
                </div>
                <div>
                  <label class="text-sm text-gray-600">{{ $t('smm.settings.platform_sort_order') }}</label>
                  <input v-model.number="platformForm.sort_order" type="number" min="0" class="w-full border rounded-lg p-2.5 mt-1 text-sm" />
                </div>
                <div class="flex items-end">
                  <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none pb-2.5">
                    <input v-model="platformForm.is_active" type="checkbox" class="h-4 w-4 rounded border-gray-300" />
                    {{ $t('smm.settings.is_active') }}
                  </label>
                </div>
              </div>

              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.settings.platform_output_schema') }}</label>
                <p class="text-xs text-gray-400 mt-0.5 mb-1">{{ $t('smm.settings.platform_output_schema_help') }}</p>
                <textarea
                  v-model="platformForm.output_schema_json"
                  rows="16"
                  class="w-full border rounded-lg p-2.5 mt-1 text-xs font-mono resize-y min-h-[16rem]"
                  spellcheck="false"
                />
              </div>

              <p v-if="platformFormError" class="text-sm text-red-600 whitespace-pre-line">{{ platformFormError }}</p>

              <div class="flex flex-wrap justify-between gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  class="px-3 py-2 text-sm rounded-lg text-red-600 hover:bg-red-50"
                  :disabled="platformSaving"
                  @click="removePlatform"
                >
                  {{ $t('common.delete') }}
                </button>
                <button
                  type="button"
                  class="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  :disabled="platformSaving || !platformForm.name.trim()"
                  @click="savePlatform"
                >
                  {{ platformSaving ? $t('common.saving') : $t('common.save') }}
                </button>
              </div>
            </div>
            <div v-else class="text-sm text-gray-500 py-10 text-center">
              {{ $t('smm.settings.platform_select') }}
            </div>
          </div>
        </template>

        <template v-else-if="section === 'business'">
          <div class="mb-4">
            <h2 class="text-lg font-semibold text-gray-900">{{ $t('smm.settings.business_title') }}</h2>
            <p class="text-sm text-gray-500 mt-0.5">{{ $t('smm.settings.business_subtitle') }}</p>
          </div>

          <div v-if="businessLoading" class="text-sm text-gray-500 py-8 text-center">{{ $t('common.loading') }}</div>
          <div v-else-if="businessLoadError" class="text-sm text-red-600 py-4">{{ businessLoadError }}</div>
          <div v-else-if="businessForm" class="space-y-4 max-w-2xl">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="sm:col-span-2">
                <label class="text-sm text-gray-600">{{ $t('smm.settings.business_company') }}</label>
                <input v-model="businessForm.company" class="w-full border rounded-lg p-2.5 mt-1 text-sm" />
              </div>
              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.settings.business_hours') }}</label>
                <input
                  v-model.number="businessForm.available_hours_per_week"
                  type="number"
                  min="1"
                  max="168"
                  class="w-full border rounded-lg p-2.5 mt-1 text-sm"
                />
              </div>
              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.settings.business_timezone') }}</label>
                <input v-model="businessForm.timezone" class="w-full border rounded-lg p-2.5 mt-1 text-sm" />
              </div>
              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.settings.business_language') }}</label>
                <input v-model="businessForm.language" class="w-full border rounded-lg p-2.5 mt-1 text-sm" />
              </div>
              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.settings.business_currency') }}</label>
                <input v-model="businessForm.currency" class="w-full border rounded-lg p-2.5 mt-1 text-sm" />
              </div>
              <div class="sm:col-span-2">
                <label class="text-sm text-gray-600">{{ $t('smm.settings.business_notes') }}</label>
                <textarea
                  v-model="businessForm.notes"
                  rows="5"
                  class="w-full border rounded-lg p-2.5 mt-1 text-sm resize-y"
                />
              </div>
            </div>
            <p v-if="businessFormError" class="text-sm text-red-600 whitespace-pre-line">{{ businessFormError }}</p>
            <div class="flex justify-end pt-2 border-t border-gray-100">
              <button
                type="button"
                class="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                :disabled="businessSaving"
                @click="saveBusiness"
              >
                {{ businessSaving ? $t('common.saving') : $t('common.save') }}
              </button>
            </div>
          </div>
        </template>

        <template v-else-if="section === 'board'">
          <board-settings-panel />
        </template>
      </section>
    </div>

    <video-preview-modal
      :show="videoPreview.show"
      :src="videoPreview.src"
      :title="videoPreview.title"
      @close="closeVideoPreview"
    />
  </div>
</template>

<script>
import SmmSubNav from '@/components/smm/SmmSubNav.vue'
import MediaUploadPanel from '@/components/smm/MediaUploadPanel.vue'
import VideoPreviewModal from '@/components/smm/VideoPreviewModal.vue'
import BoardSettingsPanel from '@/components/smm/BoardSettingsPanel.vue'

export default {
  components: {
    SmmSubNav,
    MediaUploadPanel,
    VideoPreviewModal,
    BoardSettingsPanel,
  },
  data: () => ({
    shops: [],
    selectedId: null,
    form: null,
    pendingFiles: [],
    loading: true,
    loadError: null,
    saving: false,
    formError: null,
    fileDragIndex: null,
    videoPreview: { show: false, src: '', title: '' },
    prompts: [],
    selectedPromptId: null,
    promptForm: null,
    promptsLoading: false,
    promptsLoadError: null,
    promptSaving: false,
    promptFormError: null,
    promptsLoaded: false,
    shopsLoaded: false,
    campaigns: [],
    selectedCampaignId: null,
    campaignForm: null,
    campaignsLoading: false,
    campaignsLoadError: null,
    campaignSaving: false,
    campaignFormError: null,
    campaignsLoaded: false,
    platforms: [],
    selectedPlatformId: null,
    platformForm: null,
    platformsLoading: false,
    platformsLoadError: null,
    platformSaving: false,
    platformFormError: null,
    platformsLoaded: false,
    businessForm: null,
    businessLoading: false,
    businessLoadError: null,
    businessSaving: false,
    businessFormError: null,
    businessLoaded: false,
  }),
  computed: {
    section() {
      if (this.$route.name === 'SmmSettingsPrompts') return 'prompts'
      if (this.$route.name === 'SmmSettingsCampaigns') return 'campaigns'
      if (this.$route.name === 'SmmSettingsPlatforms') return 'platforms'
      if (this.$route.name === 'SmmSettingsBusiness') return 'business'
      if (this.$route.name === 'SmmSettingsBoard') return 'board'
      return 'channels'
    },
    menuItems() {
      return [
        { id: 'channels', route: 'SmmSettingsChannels', label: this.$t('smm.settings.menu_channels') },
        { id: 'prompts', route: 'SmmSettingsPrompts', label: this.$t('smm.settings.menu_prompts') },
        { id: 'campaigns', route: 'SmmSettingsCampaigns', label: this.$t('smm.settings.menu_campaigns') },
        { id: 'platforms', route: 'SmmSettingsPlatforms', label: this.$t('smm.settings.menu_platforms') },
        { id: 'business', route: 'SmmSettingsBusiness', label: this.$t('smm.settings.menu_business') },
        { id: 'board', route: 'SmmSettingsBoard', label: this.$t('smm.settings.menu_board') },
        { id: 'logs', route: 'SmmSettingsLogs', label: this.$t('smm.settings.menu_logs') },
      ]
    },
  },
  watch: {
    section: {
      immediate: true,
      handler(value) {
        if (value === 'prompts' && !this.promptsLoaded) {
          this.loadPrompts()
        }
        if (value === 'channels' && !this.shopsLoaded) {
          this.loadShops()
        }
        if (value === 'campaigns' && !this.campaignsLoaded) {
          this.loadCampaigns()
        }
        if (value === 'platforms' && !this.platformsLoaded) {
          this.loadPlatforms()
        }
        if (value === 'business' && !this.businessLoaded) {
          this.loadBusiness()
        }
      },
    },
  },
  methods: {
    formatValidationError(e) {
      const data = e.response?.data
      if (!data) return null
      if (data.message) return data.message
      if (data.errors) return Object.values(data.errors).flat().join('\n')
      return null
    },
    formatCampaignDate(value) {
      if (!value) return ''
      const date = String(value).slice(0, 10)
      try {
        return new Date(`${date}T00:00:00`).toLocaleDateString(
          this.$i18n.locale === 'ru' ? 'ru-RU' : 'en-US',
          { day: 'numeric', month: 'short', year: 'numeric' }
        )
      } catch (_) {
        return date
      }
    },
    formatCampaignDates(campaign) {
      const start = this.formatCampaignDate(campaign.starts_on)
      if (!campaign.ends_on) return start
      return `${start} – ${this.formatCampaignDate(campaign.ends_on)}`
    },
    toDateInput(value) {
      if (!value) return ''
      return String(value).slice(0, 10)
    },
    async loadCampaigns() {
      this.campaignsLoading = true
      this.campaignsLoadError = null
      try {
        const { data } = await axios.get('/api/v1/smm/campaigns', { params: { all: 1 } })
        this.campaigns = (data.data ?? []).map((c) => ({
          ...c,
          id: Number(c.id),
        }))
        this.campaignsLoaded = true
        if (this.selectedCampaignId) {
          const still = this.campaigns.find((c) => c.id === this.selectedCampaignId)
          if (still) this.selectCampaign(still.id)
          else {
            this.selectedCampaignId = null
            this.campaignForm = null
          }
        }
        if (!this.selectedCampaignId && !this.campaignForm && this.campaigns.length) {
          this.selectCampaign(this.campaigns[0].id)
        }
      } catch (e) {
        this.campaignsLoadError = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.campaignsLoading = false
      }
    },
    selectCampaign(id) {
      const campaign = this.campaigns.find((c) => Number(c.id) === Number(id))
      if (!campaign) return
      this.selectedCampaignId = Number(campaign.id)
      this.campaignFormError = null
      this.campaignForm = {
        id: campaign.id,
        name: campaign.name || '',
        starts_on: this.toDateInput(campaign.starts_on),
        ends_on: this.toDateInput(campaign.ends_on),
        description: campaign.description || '',
        priority: campaign.priority ?? 5,
        is_active: campaign.is_active !== false,
      }
    },
    createCampaign() {
      this.selectedCampaignId = null
      this.campaignFormError = null
      this.campaignForm = {
        id: null,
        name: '',
        starts_on: '',
        ends_on: '',
        description: '',
        priority: 5,
        is_active: true,
      }
    },
    async saveCampaign() {
      if (!this.campaignForm?.name?.trim() || !this.campaignForm.starts_on) return
      this.campaignSaving = true
      this.campaignFormError = null
      try {
        const payload = {
          name: this.campaignForm.name.trim(),
          starts_on: this.campaignForm.starts_on,
          ends_on: this.campaignForm.ends_on || null,
          description: this.campaignForm.description || null,
          priority: Number(this.campaignForm.priority) || 5,
          is_active: !!this.campaignForm.is_active,
        }

        let campaignId = this.campaignForm.id
        if (campaignId) {
          await axios.patch(`/api/v1/smm/campaigns/${campaignId}`, payload)
        } else {
          const { data } = await axios.post('/api/v1/smm/campaigns', payload)
          campaignId = data.data.id
        }

        this.campaignsLoaded = false
        await this.loadCampaigns()
        this.selectCampaign(campaignId)
      } catch (e) {
        this.campaignFormError = this.formatValidationError(e) || this.$t('common.save_error')
      } finally {
        this.campaignSaving = false
      }
    },
    async removeCampaign() {
      if (!this.campaignForm?.id) {
        this.campaignForm = null
        return
      }
      if (!confirm(this.$t('smm.settings.campaign_delete_confirm', { name: this.campaignForm.name }))) return
      this.campaignSaving = true
      try {
        await axios.delete(`/api/v1/smm/campaigns/${this.campaignForm.id}`)
        this.campaignForm = null
        this.selectedCampaignId = null
        this.campaignsLoaded = false
        await this.loadCampaigns()
      } catch (e) {
        this.campaignFormError = this.formatValidationError(e) || this.$t('common.delete_error')
      } finally {
        this.campaignSaving = false
      }
    },
    async loadPlatforms() {
      this.platformsLoading = true
      this.platformsLoadError = null
      try {
        const { data } = await axios.get('/api/v1/smm/platforms', { params: { all: 1 } })
        this.platforms = (data.data ?? []).map((p) => ({
          ...p,
          id: Number(p.id),
        }))
        this.platformsLoaded = true
        if (this.selectedPlatformId) {
          const still = this.platforms.find((p) => p.id === this.selectedPlatformId)
          if (still) this.selectPlatform(still.id)
          else this.selectedPlatformId = null
        }
        if (!this.selectedPlatformId && !this.platformForm && this.platforms.length) {
          this.selectPlatform(this.platforms[0].id)
        }
      } catch (e) {
        this.platformsLoadError = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.platformsLoading = false
      }
    },
    selectPlatform(id) {
      const platform = this.platforms.find((p) => Number(p.id) === Number(id))
      if (!platform) return
      this.selectedPlatformId = Number(platform.id)
      this.platformFormError = null
      this.platformForm = {
        id: platform.id,
        name: platform.name || '',
        slug: platform.slug || '',
        sort_order: platform.sort_order ?? 0,
        is_active: platform.is_active !== false,
        output_schema_json: JSON.stringify(platform.output_schema || {}, null, 2),
      }
    },
    createPlatform() {
      this.selectedPlatformId = null
      this.platformFormError = null
      this.platformForm = {
        id: null,
        name: '',
        slug: '',
        sort_order: (this.platforms.length + 1),
        is_active: true,
        output_schema_json: JSON.stringify({
          type: 'object',
          properties: {
            metadata: { type: 'object' },
            content: { type: 'object' },
            platform_specific: { type: 'object' },
          },
          required: ['metadata', 'content', 'platform_specific'],
        }, null, 2),
      }
    },
    async savePlatform() {
      if (!this.platformForm?.name?.trim()) return
      this.platformSaving = true
      this.platformFormError = null
      try {
        let schema = null
        const raw = String(this.platformForm.output_schema_json || '').trim()
        if (raw) {
          try {
            schema = JSON.parse(raw)
          } catch (_) {
            this.platformFormError = this.$t('smm.settings.platform_schema_invalid')
            return
          }
          if (schema === null || typeof schema !== 'object' || Array.isArray(schema)) {
            this.platformFormError = this.$t('smm.settings.platform_schema_invalid')
            return
          }
        }

        const payload = {
          name: this.platformForm.name.trim(),
          slug: this.platformForm.slug.trim() || null,
          output_schema: schema,
          sort_order: Number(this.platformForm.sort_order) || 0,
          is_active: !!this.platformForm.is_active,
        }

        let platformId = this.platformForm.id
        if (platformId) {
          await axios.patch(`/api/v1/smm/platforms/${platformId}`, payload)
        } else {
          const { data } = await axios.post('/api/v1/smm/platforms', payload)
          platformId = data.data.id
        }

        this.platformsLoaded = false
        await this.loadPlatforms()
        this.selectPlatform(platformId)
      } catch (e) {
        this.platformFormError = this.formatValidationError(e) || this.$t('common.save_error')
      } finally {
        this.platformSaving = false
      }
    },
    async removePlatform() {
      if (!this.platformForm?.id) {
        this.platformForm = null
        return
      }
      if (!confirm(this.$t('smm.settings.platform_delete_confirm', { name: this.platformForm.name }))) return
      this.platformSaving = true
      try {
        await axios.delete(`/api/v1/smm/platforms/${this.platformForm.id}`)
        this.platformForm = null
        this.selectedPlatformId = null
        this.platformsLoaded = false
        await this.loadPlatforms()
      } catch (e) {
        this.platformFormError = this.formatValidationError(e) || this.$t('common.delete_error')
      } finally {
        this.platformSaving = false
      }
    },
    async loadBusiness() {
      this.businessLoading = true
      this.businessLoadError = null
      try {
        const { data } = await axios.get('/api/v1/smm/business-context')
        const ctx = data.data || {}
        this.businessForm = {
          company: ctx.company || '',
          available_hours_per_week: ctx.available_hours_per_week ?? 12,
          timezone: ctx.timezone || 'Europe/Warsaw',
          language: ctx.language || 'en',
          currency: ctx.currency || 'USD',
          notes: ctx.notes || '',
        }
        this.businessLoaded = true
      } catch (e) {
        this.businessLoadError = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.businessLoading = false
      }
    },
    async saveBusiness() {
      if (!this.businessForm) return
      this.businessSaving = true
      this.businessFormError = null
      try {
        const { data } = await axios.patch('/api/v1/smm/business-context', {
          company: this.businessForm.company.trim() || null,
          available_hours_per_week: Number(this.businessForm.available_hours_per_week) || 12,
          timezone: this.businessForm.timezone.trim() || 'Europe/Warsaw',
          language: this.businessForm.language.trim() || 'en',
          currency: this.businessForm.currency.trim() || 'USD',
          notes: this.businessForm.notes || null,
        })
        const ctx = data.data || {}
        this.businessForm = {
          company: ctx.company || '',
          available_hours_per_week: ctx.available_hours_per_week ?? 12,
          timezone: ctx.timezone || 'Europe/Warsaw',
          language: ctx.language || 'en',
          currency: ctx.currency || 'USD',
          notes: ctx.notes || '',
        }
      } catch (e) {
        this.businessFormError = this.formatValidationError(e) || this.$t('common.save_error')
      } finally {
        this.businessSaving = false
      }
    },
    async loadPrompts() {
      this.promptsLoading = true
      this.promptsLoadError = null
      try {
        const { data } = await axios.get('/api/v1/smm/system-prompts')
        this.prompts = (data.data ?? []).map((p) => ({
          ...p,
          id: Number(p.id),
        }))
        this.promptsLoaded = true
        if (this.selectedPromptId) {
          const still = this.prompts.find((p) => p.id === this.selectedPromptId)
          if (still) this.selectPrompt(still.id)
          else this.selectedPromptId = null
        }
        if (!this.selectedPromptId && this.prompts.length) {
          this.selectPrompt(this.prompts[0].id)
        }
      } catch (e) {
        this.promptsLoadError = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.promptsLoading = false
      }
    },
    selectPrompt(id) {
      const prompt = this.prompts.find((p) => Number(p.id) === Number(id))
      if (!prompt) return
      this.selectedPromptId = Number(prompt.id)
      this.promptFormError = null
      this.promptForm = {
        id: prompt.id,
        name: prompt.name || '',
        slug: prompt.slug || '',
        description: prompt.description || '',
        body: prompt.body || '',
      }
    },
    async savePrompt() {
      if (!this.promptForm?.id) return
      this.promptSaving = true
      this.promptFormError = null
      try {
        const { data } = await axios.patch(`/api/v1/smm/system-prompts/${this.promptForm.id}`, {
          name: this.promptForm.name.trim(),
          description: this.promptForm.description.trim() || null,
          body: this.promptForm.body,
        })
        const updated = data.data
        const idx = this.prompts.findIndex((p) => p.id === this.promptForm.id)
        if (idx !== -1) {
          this.prompts.splice(idx, 1, { ...updated, id: Number(updated.id) })
        }
        this.selectPrompt(this.promptForm.id)
      } catch (e) {
        this.promptFormError = this.formatValidationError(e) || this.$t('common.save_error')
      } finally {
        this.promptSaving = false
      }
    },
    async loadShops() {
      this.loading = true
      this.loadError = null
      try {
        const { data } = await axios.get('/api/v1/smm/shops', {
          params: { all: 1, with_files: 1 },
        })
        this.shops = (data.data ?? []).map((s) => ({
          ...s,
          id: Number(s.id),
          files: s.files ?? [],
        }))
        if (this.selectedId) {
          const still = this.shops.find((s) => s.id === this.selectedId)
          if (still) this.selectChannel(still.id)
          else this.selectedId = null
        }
        if (!this.selectedId && this.shops.length) {
          this.selectChannel(this.shops[0].id)
        }
      } catch (e) {
        this.loadError = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.shopsLoaded = true
        this.loading = false
      }
    },
    selectChannel(id) {
      const shop = this.shops.find((s) => Number(s.id) === Number(id))
      if (!shop) return
      this.selectedId = Number(shop.id)
      this.pendingFiles = []
      this.formError = null
      this.$refs.uploadPanel?.clear?.()
      this.form = {
        id: shop.id,
        name: shop.name || '',
        slug: shop.slug || '',
        color: shop.color || '#111827',
        homepage_url: shop.homepage_url || '',
        base_prompt: shop.base_prompt || '',
        is_active: shop.is_active !== false,
        files: [...(shop.files || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
      }
    },
    createChannel() {
      this.selectedId = null
      this.pendingFiles = []
      this.formError = null
      this.form = {
        id: null,
        name: '',
        slug: '',
        color: '#111827',
        homepage_url: '',
        base_prompt: '',
        is_active: true,
        files: [],
      }
      this.$nextTick(() => this.$refs.uploadPanel?.clear?.())
    },
    async saveChannel() {
      if (!this.form?.name?.trim()) return
      this.saving = true
      this.formError = null
      try {
        const payload = {
          name: this.form.name.trim(),
          slug: this.form.slug.trim() || null,
          color: this.form.color || null,
          homepage_url: this.form.homepage_url.trim() || null,
          base_prompt: this.form.base_prompt || null,
          is_active: !!this.form.is_active,
        }

        let shopId = this.form.id
        if (shopId) {
          const { data } = await axios.patch(`/api/v1/smm/shops/${shopId}`, payload)
          this.form.files = data.data?.files ?? this.form.files
        } else {
          const { data } = await axios.post('/api/v1/smm/shops', payload)
          shopId = data.data.id
          this.form.id = shopId
          this.selectedId = Number(shopId)
        }

        if (this.pendingFiles.length) {
          const fd = new FormData()
          this.pendingFiles.forEach((item, i) => fd.append(`files[${i}]`, item.file))
          const { data } = await axios.post(`/api/v1/smm/shops/${shopId}/files`, fd)
          if (data.data?.length) {
            this.form.files = [...(this.form.files || []), ...data.data]
          }
          this.pendingFiles = []
          this.$refs.uploadPanel?.clear?.()
        }

        await this.loadShops()
        this.selectChannel(shopId)
      } catch (e) {
        this.formError = this.formatValidationError(e) || this.$t('common.save_error')
      } finally {
        this.saving = false
      }
    },
    async removeChannel() {
      if (!this.form?.id) {
        this.form = null
        return
      }
      if (!confirm(this.$t('smm.settings.channel_delete_confirm', { name: this.form.name }))) return
      this.saving = true
      try {
        await axios.delete(`/api/v1/smm/shops/${this.form.id}`)
        this.form = null
        this.selectedId = null
        await this.loadShops()
      } catch (e) {
        this.formError = this.formatValidationError(e) || this.$t('common.delete_error')
      } finally {
        this.saving = false
      }
    },
    onFileDragStart(index, event) {
      this.fileDragIndex = index
      event.dataTransfer.effectAllowed = 'move'
      try {
        event.dataTransfer.setData('text/plain', String(index))
      } catch (_) {
        // ignore
      }
    },
    async onFileDrop(index) {
      if (this.fileDragIndex == null || this.fileDragIndex === index || !this.form?.id) {
        this.fileDragIndex = null
        return
      }
      const next = [...this.form.files]
      const [moved] = next.splice(this.fileDragIndex, 1)
      next.splice(index, 0, moved)
      this.fileDragIndex = null
      this.form.files = next.map((f, i) => ({ ...f, sort_order: i }))

      try {
        const { data } = await axios.post(`/api/v1/smm/shops/${this.form.id}/files/reorder`, {
          file_ids: this.form.files.map((f) => f.id),
        })
        if (data.data?.files) {
          this.form.files = data.data.files
          const shop = this.shops.find((s) => s.id === this.form.id)
          if (shop) shop.files = data.data.files
        }
      } catch (e) {
        this.formError = this.formatValidationError(e) || this.$t('smm.settings.reorder_error')
      }
    },
    async deleteFile(file) {
      if (!this.form?.id || !confirm(this.$t('smm.media.delete_file_confirm'))) return
      try {
        await axios.delete(`/api/v1/smm/shops/${this.form.id}/files/${file.id}`)
        this.form.files = this.form.files.filter((f) => f.id !== file.id)
        const shop = this.shops.find((s) => s.id === this.form.id)
        if (shop) shop.files = this.form.files
      } catch (e) {
        this.formError = this.formatValidationError(e) || this.$t('common.delete_error')
      }
    },
    openVideoPreview({ src, title }) {
      this.videoPreview = { show: true, src, title: title || this.$t('smm.media.video') }
    },
    closeVideoPreview() {
      this.videoPreview = { show: false, src: '', title: '' }
    },
  },
}
</script>
